import { SOLUTIONS_NL } from '@energiebespaarders/constants';
import { Box, Card, Container, Flex, Image, theme } from '@energiebespaarders/symbols';
import { Book } from '@energiebespaarders/symbols/helpers';
import Head from 'next/head';
import { useCallback } from 'react';
import styled from 'styled-components';
import ErrorBoundary from '~/components/ErrorBoundary';
import HybridLink from '~/components/HybridLink';
import { SOLUTION_ILLUSTRATIONS } from '~/utils/constants';
import type { Solution } from '@energiebespaarders/constants';

const StyledH1 = styled.h1`
  font-size: ${theme.type.scale[3]};
  font-weight: 600;
  margin-top: 0px;
  margin-bottom: 0.5em;
  font-family: Causten, sans-serif;

  @media (min-width: ${theme.breakpoints[2]}) {
    font-size: ${theme.type.scale[2]};
  }
`;

const BaselineProductPage: React.FC = () => {
  const getSolutionLink = useCallback((s: Solution) => {
    switch (s) {
      case 'pvSystem':
      case 'wallInsulation':
      case 'floorInsulation':
      case 'centralHeatingBoiler':
        return SOLUTIONS_NL[s].toLowerCase();
      case 'crawlspaceInsulation':
        return 'vloerisolatie';
      case 'atticFloorInsulation':
      case 'innerRoofInsulation':
        return 'dakisolatie';
      case 'hybridHeatPump':
      case 'electricHeatPump':
        return 'warmtepomp';
      case 'windowFrames':
      case 'windowGlazing':
        return 'isolatieglas';
      default:
        return '';
    }
  }, []);

  const customSolutionLinks: Record<Solution, string | null> = {
    centralHeatingBoiler: SOLUTIONS_NL.centralHeatingBoiler,
    hybridHeatPump: 'Warmtepompen (hybride en all-electric)',
    wallInsulation: SOLUTIONS_NL.wallInsulation,
    floorInsulation: 'Vloer- en bodemisolatie',
    innerRoofInsulation: 'Dakisolatie (binnen- en buiten)',
    pvSystem: SOLUTIONS_NL.pvSystem,
    windowGlazing: 'Isolatieglas en kozijnen',
    atticFloorInsulation: null,
    chargingBox: null,
    crawlspaceInsulation: null,
    electricHeatPump: null,
    airToAirHeatPump: null,
    miscellaneous: null,
    sedum: null,
    windowFrames: null,
    underfloorHeating: null,
    homeBattery: null,
  };
  return (
    <ErrorBoundary>
      <Head>
        <link
          rel="canonical"
          href="https://energiebespaarders.nl/woning-verduurzamen/product"
          key="canonical"
        />
      </Head>
      <Flex flexWrap="wrap" px={2}>
        <Box width={1}>
          <Container size="lg" color="grayDarker" mt={3} py={10}>
            <Flex flexWrap="wrap" justifyContent="center" mx={-1}>
              <Box width={1} px={1} mb={5}>
                <StyledH1>Alle oplossingen voor een duurzamer huis</StyledH1>
                <p>
                  Bij ons ben je aan je het juiste adres van maar liefst tien energiebesparende
                  oplossingen.
                </p>
              </Box>
              {(Object.keys(customSolutionLinks) as Solution[]).map(
                solution =>
                  customSolutionLinks[solution] && (
                    <Box key={solution} width={[1, 1, 1 / 2]} px={1} pb={2}>
                      <HybridLink to={`/woning-verduurzamen/product/${getSolutionLink(solution)}`}>
                        <Card hoverShadow="xl" color="grayDarker">
                          <Flex mx={-2} alignItems="center">
                            <Box width={1 / 4} px={2}>
                              <Image
                                src={SOLUTION_ILLUSTRATIONS[solution]}
                                alt={`Lees meer over ${SOLUTIONS_NL[solution]}`}
                                radius="100%"
                              />
                            </Box>
                            <Box width={3 / 4} px={2}>
                              <Book>{customSolutionLinks[solution]}</Book>
                            </Box>
                          </Flex>
                        </Card>
                      </HybridLink>
                    </Box>
                  ),
              )}
            </Flex>
          </Container>
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};

export default BaselineProductPage;
